/* eslint-disable @typescript-eslint/no-explicit-any */
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { core, safeEnumCompare } from '@tock/coretypes';
import { getBeautifiedPhoneNumber } from './phoneUtils';
import { logger } from '@tock/logging';
import { pluralize } from './pluralize';
import protos from 'utils/protos';

const { Patron } = protos;

export type SlimPatron =
  | Pick<core.IPatron, 'id' | 'firstName' | 'lastName' | 'email'>
  | core.IPatronPublic
  | null
  | undefined;
export function getEmailId(patronDetails: SlimPatron) {
  return oc(patronDetails).email('').split('@')[0];
}

export function getFullName(patron: SlimPatron, def = '-'): string {
  if (!patron) {
    // logger.debug('getFullName called without patron');
    return '';
  }
  let fullName = def;
  const firstName = _.trim(oc(patron).firstName() || '');
  const lastName = _.trim(oc(patron).lastName() || '');

  if (!_.isEmpty(firstName) || !_.isEmpty(lastName)) {
    fullName = _.trim(`${firstName} ${lastName}`);
  }

  return fullName;
}

interface PatronName {
  firstName?: string | null;
  lastName?: string | null;
}

export function getFirstNameLastInitial(patron: PatronName | null): string {
  if (!patron) {
    logger.debug('getFirstNameLastInitial called without patron');
    return '';
  }

  let firstnameLastInitial = '-';
  const firstName = _.trim(oc(patron).firstName(''));
  const lastName = _.trim(oc(patron).lastName(''));

  if (!_.isEmpty(firstName) || !_.isEmpty(lastName)) {
    firstnameLastInitial = _.trim(`${firstName} ${lastName.slice(0, 1)}.`);
  }

  return firstnameLastInitial;
}

export function getFullNameOrEmail(patron: core.Patron | core.IPatron | null | undefined): string {
  if (!patron) {
    logger.debug('getFullNameOrEmail called without patron');
    return '';
  }

  const firstName = _.trim(oc(patron).firstName(''));
  const lastName = _.trim(oc(patron).lastName(''));

  if (!_.isEmpty(firstName) || !_.isEmpty(lastName)) {
    return _.trim(`${firstName} ${lastName}`);
  }

  return patron?.email ?? '';
}

export function hasCompleteProfile(patron: any): boolean {
  return (
    !!patron &&
    !!patron.firstName &&
    !!patron.lastName &&
    !!patron.phone &&
    !!patron.phoneCountryCode &&
    !!patron.isoCountryCode &&
    !!patron.zipCode
  );
}

export function hasFullName(patron: core.Patron): boolean {
  return !!patron && !!patron.firstName && !!patron.lastName;
}

export function hasAnyName(patron: core.IPatron | core.IPatronPublic | null | undefined): boolean {
  if (!patron) {
    logger.debug('getFullName called without patron');
    return false;
  }
  const firstName = oc(patron).firstName();
  const lastName = oc(patron).lastName();

  return !_.isEmpty(firstName) || !_.isEmpty(lastName);
}

/**
 * This is a work-around for the fact that Party is a dashboard/service
 * specific type but this file is in `shared`. We can maybe fix that nowadays.
 */
export interface Party {
  id: number;
  primaryDinerProfile?: {
    patron?: {
      id?: number | null;
    } | null;
  };
}

export function getPhoneNumber(
  phoneCountryCode: string | null | undefined,
  phone: string | null | undefined
): string | null {
  // checking for at least 2 characters here because we found some parties just have the
  // number `1` in this field.
  if (!phone || phone.length <= 2) {
    return null;
  }
  let code = phoneCountryCode || '';
  let beautifiedPhone = (phone || '').replace(/[^0-9]/g, '');
  if (phoneCountryCode && phoneCountryCode !== '') {
    const firstSpace = phoneCountryCode.indexOf(' ');
    if (firstSpace !== -1) {
      // phoneCountryCode is in the format "+1 US"
      // For now we are only adding the "+1"
      // Can add in the country by adding ${phoneCountryCode.slice(firstSpace, phoneCountryCode.length)}
      code = `${phoneCountryCode.slice(0, firstSpace)}`;
    }
    // If phone number does not contain dashes and should be using NANP format, beautify it
    if (!/(-)/.test(phone) && phoneCountryCode.indexOf('+1') === 0) {
      beautifiedPhone = getBeautifiedPhoneNumber(beautifiedPhone);
      return `${code} ${beautifiedPhone}`;
    }
    return `${code} ${phone}`;
  } else {
    return getBeautifiedPhoneNumber(beautifiedPhone);
  }
}

export function toGuestsString(numGuests = 0) {
  return pluralize(numGuests, 'guest');
}

export function toGuestsStringCapitalized(numGuests = 0) {
  return pluralize(numGuests, 'Guest');
}

export function requiresEmailConfirmation(patron: core.IPatron) {
  return safeEnumCompare(
    patron?.status ?? Patron.Status.NEW_USER_REQUIRES_CONFIRMATION,
    Patron.Status,
    Patron.Status.NEW_USER_REQUIRES_CONFIRMATION
  );
}

export function toSearchResultLabel(patron: core.IPatron): string {
  return `${getFullName(patron)} (${oc(patron).email('No email')}, ${
    oc(patron).phone('') || 'No phone'
  })`;
}

interface CheckForMissingPatronFieldsOptions {
  patron?: core.Patron;
  exclude?: string[];
}

export function checkForMissingPatronFields({
  patron,
  exclude,
}: CheckForMissingPatronFieldsOptions): Dictionary<boolean> {
  const missingFields: Dictionary<boolean> = {};

  if (!patron) {
    return missingFields;
  }
  if (_.size(patron.email) <= 0) {
    missingFields.email = true;
  }

  if (_.size(patron.firstName) <= 0) {
    missingFields.firstName = true;
  }

  if (_.size(patron.lastName) <= 0) {
    missingFields.lastName = true;
  }

  if (_.size(patron.phone) <= 0) {
    missingFields.phone = true;
  }

  if (_.size(patron.phoneCountryCode) <= 0) {
    missingFields.phoneCountryCode = true;
  }

  if (_.size(patron.zipCode) <= 0) {
    missingFields.zipCode = true;
  }

  if (exclude) {
    return _.omit(missingFields, exclude);
  }
  return missingFields;
}

export const capitalizeName = (value: string | undefined) => {
  if (!value) {
    return value;
  }

  let retValue = '';
  for (let i = 0; i < value.length; ++i) {
    if (
      i === 0 ||
      value[i - 1] === ' ' ||
      (value[i - 1] === "'" && (!value[i - 2] || value[i + 1] || value[i - 2] === ' ')) ||
      value[i - 1] === '.' ||
      value[i - 1] === '-'
    ) {
      retValue += _.toUpper(value[i]);
    } else {
      retValue += value[i];
    }
  }
  return retValue;
};

export function isPatronSiteBuilder(patron: core.IPatron | null | undefined) {
  return (
    _.startsWith(patron?.email ?? '', 'tock.sitebuilders.') &&
    _.endsWith(patron?.email ?? '', '@gmail.com')
  );
}

export function convertFullNameToFirstLast(
  name: string | undefined,
  { reverse = false }: { reverse: boolean }
): { firstName: string; lastName: string } {
  let firstName = '';
  let lastName = '';

  if (name && !/^\s*$/.test(name)) {
    let rest;
    const splitParts = name.trim().split(' ');

    if (splitParts.length === 1) {
      reverse ? (firstName = splitParts[0]) : (lastName = splitParts[0]);
    } else {
      [firstName, ...rest] = splitParts;
      lastName = rest.join(' ');
    }
  }

  return {
    firstName: capitalizeName(firstName) ?? '',
    lastName: capitalizeName(lastName) ?? '',
  };
}
